import {
  retryDetailMonitorLog,
  getMonitorFilterConfig,
  getMonitorTemplates,
  queryMonitorList,
  queryMonitorStats,
} from "@/api/monitors";

const state = {
  current_monitor_object: null,
  monitor_overall_stats: null,
  monitor_overall_stats_loading: false,
  _monitor_overall_stats_controller: null,
  monitor_filter_config: null,
  monitor_templates: null,

  _query_list_controller: null,
  _filter_controller: null,
  query_list_loading: false,
  query_list_options: {},
  query_list_pagination: {
    page: 1,
  },
  query_list: [],
  _template_controller: null,

  // Wizard tour (driver.js): nonce bumps to start the tour; step pins the
  // create-wizard's current_step_index while the tour is walking through it.
  wizard_tour_nonce: 0,
  wizard_tour_step: null,
};

const mutations = {
  set_current_monitor_object(state, data) {
    state.current_monitor_object = data;
  },
  set_monitor_overall_stats(state, data) {
    state.monitor_overall_stats = data;
  },
  set_monitor_filter_config(state, data) {
    state.monitor_filter_config = data;
  },
  set_monitor_templates(state, data) {
    state.monitor_templates = data;
  },
  set_query_list_controller(state, controller) {
    state._query_list_controller = controller;
  },
  set_query_list_loading(state, loading) {
    state.query_list_loading = loading;
  },
  set_query_list_options(state, options) {
    state.query_list_options = options;
  },
  set_query_list_pagination(state, pagination) {
    state.query_list_pagination = pagination;
  },
  set_query_list(state, list) {
    state.query_list = list;
  },
  set_filter_controller(state, controller) {
    state._filter_controller = controller;
  },
  set_template_controller(state, controller) {
    state._template_controller = controller;
  },
  set_monitor_overall_stats_controller(state, controller) {
    state._monitor_overall_stats_controller = controller;
  },
  set_monitor_overall_stats_loading(state, loading) {
    state.monitor_overall_stats_loading = loading;
  },
  trigger_wizard_tour(state) {
    state.wizard_tour_nonce += 1;
  },
  set_wizard_tour_step(state, step) {
    state.wizard_tour_step = step;
  },
};

const actions = {
  async retryMonitorLog(context, data) {
    const res = await retryDetailMonitorLog({
      profile_id: this.state.user.currentId.profile_id,
      ...data,
    });

    if (res) {
      return res;
    }
  },
  async queryMonitorOverallStats(context, data) {
    try {
      if (context.state._monitor_overall_stats_controller) {
        context.state._monitor_overall_stats_controller.abort();
      }
      const controller = new AbortController();
      context.commit("set_monitor_overall_stats_controller", controller);
      context.commit("set_monitor_overall_stats_loading", true);
      const res = await queryMonitorStats(
        {
          profile_id: this.state.user.currentId.profile_id,
        },
        { signal: controller.signal },
      );
      context.commit("set_monitor_overall_stats", res?.data || null);
      context.commit("set_monitor_overall_stats_loading", false);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Monitor overall stats query aborted");
      } else {
        console.error("Failed to query monitor overall stats:", error);

        context.commit("set_monitor_overall_stats_loading", false);
        context.commit("set_monitor_overall_stats", null);
      }
    }
  },
  async queryMonitorFilterConfig(context, data) {
    if (context.state._filter_controller) {
      context.state._filter_controller.abort();
    }
    const controller = new AbortController();
    context.commit("set_filter_controller", controller);
    const res = await getMonitorFilterConfig(
      {
        profile_id: this.state.user.currentId.profile_id,
        ...data,
      },
      {
        signal: controller.signal,
      },
    );

    if (res && res.data) {
      context.commit("set_monitor_filter_config", res.data);
      return res.data;
    }
  },
  async queryMonitorTemplates(context, data) {
    if (context.state._template_controller) {
      context.state._template_controller.abort();
    }
    const controller = new AbortController();
    context.commit("set_template_controller", controller);
    const res = await getMonitorTemplates(
      {
        profile_id: this.state.user.currentId.profile_id,
        ...data,
      },
      {
        signal: controller.signal,
      },
    );

    if (res && res.data) {
      context.commit("set_monitor_templates", res.data);
      return res.data;
    }
  },

  async queryMonitorListWithPaginationFilters(context, data) {
    const { state, commit } = context;

    const {
      _query_list_controller,
      query_list_pagination,
      current_monitor_object,
    } = state;

    const { options = {}, silent = false } = data || {};
    try {
      if (_query_list_controller) {
        _query_list_controller.abort();
      }
      const controller = new AbortController();
      commit("set_query_list_controller", controller);
      if (!silent) {
        commit("set_query_list_loading", true);
      }

      const res = await queryMonitorList(
        {
          page: query_list_pagination.page,
          profile_id: this.state.user.currentId.profile_id,
          ...state.query_list_options,
          ...options,
        },
        { signal: controller.signal },
      );
      commit("set_query_list_options", {
        ...state.query_list_options,
        ...options,
      });

      if (res && res.data) {
        const { monitors, pagination } = res.data;
        commit("set_query_list_pagination", pagination);
        commit("set_query_list", monitors);

        if (current_monitor_object) {

          const current = monitors.find(
            (item) => item.monitor_id === current_monitor_object.monitor_id,
          );
          if (!current) {
            commit("set_current_monitor_object", null);
          } else {
            commit("set_current_monitor_object", current);
          }
        }
        if (!silent) {
          commit("set_query_list_loading", false);
        }
        return monitors;
      }
      if (!silent) {
        commit("set_query_list_loading", false);
      }
    } catch (error) {
      if (error.code === "ERR_CANCELED") {
        console.log("Monitor list query aborted");
      } else {
        console.error("Failed to query monitor list:", error);
        commit("set_query_list", []);
        commit("set_query_list_pagination", { page: 1 });
        if (!silent) {
          commit("set_query_list_loading", false);
        }
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
